/**
 * CORE-1733 — откуда пришёл фокус: клавиатура или указатель.
 *
 * Текстовые поля совпадают с `:focus-visible` и при клике мышью, поэтому
 * кольцо на них по CSS одной только псевдоклассом зажигалось бы на каждый клик
 * (владелец просил так не делать). Для клавиатуры же каретка — не различимый
 * индикатор фокуса. Помечаем корень документа: `data-focus-source="keyboard"`
 * после Tab / стрелок, `"pointer"` после мыши, пальца или стилуса; правило
 * кольца для полей в public/global.css смотрит на эту пометку.
 */
const NAV_KEYS = new Set(['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);

type KeyLike = {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  target?: EventTarget | null;
};

/** Поле, где стрелки и Home/End двигают каретку, а не фокус. */
function isTextEntry(target: EventTarget | null | undefined): boolean {
  const el = target as { tagName?: string; isContentEditable?: boolean } | null | undefined;
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable === true;
}

export function focusSourceForKey(e: KeyLike): 'keyboard' | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (!NAV_KEYS.has(e.key)) return null;
  // Кликнули в поле мышью и двигают каретку стрелками — фокус не уходил, и
  // кольцо на поле зажглось бы ровно там, где владелец просил его не рисовать.
  if (e.key !== 'Tab' && isTextEntry(e.target)) return null;
  return 'keyboard';
}

let installed = false;

export function installFocusSource(doc: Document | undefined = typeof document !== 'undefined' ? document : undefined): void {
  if (!doc || installed) return;
  installed = true;
  const root = doc.documentElement;
  const mark = (source: 'keyboard' | 'pointer') => {
    if (root.getAttribute('data-focus-source') !== source) root.setAttribute('data-focus-source', source);
  };
  // capture: раньше обработчиков компонентов, которые могут остановить всплытие.
  doc.addEventListener('keydown', (e) => { if (focusSourceForKey(e)) mark('keyboard'); }, true);
  doc.addEventListener('pointerdown', () => mark('pointer'), true);
  doc.addEventListener('mousedown', () => mark('pointer'), true);
  doc.addEventListener('touchstart', () => mark('pointer'), { capture: true, passive: true });
}
